import { useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { SchemaForm as SharedForm, type FieldNode } from '@captomi/schema-form';
import { OFFICE_WIDGETS } from '@captomi/office-ui';

// Schema-driven editor for an app's tenant-scoped app-data. Fetches the compiled FieldNode tree
// (GET /api/apps/:app_code/schema) + the data (GET /api/tenants/:tid/apps/:app_code), renders a
// widget per field, and saves only the edited fields (PATCH). The BFF is the write gate; here
// `never`-editable fields render read-only. No per-field React — adding a field to the schema
// makes it appear here automatically (docs/archive/TENANT_ADMIN_SPEC.md Topic 9).

interface SchemaResp {
  schema: FieldNode;
}
interface DataResp {
  app_code: string;
  data: Record<string, unknown>;
}

type Obj = Record<string, unknown>;

function get_path(obj: Obj, path: string): unknown {
  return path.split('.').reduce<unknown>((o, k) => (o && typeof o === 'object' ? (o as Obj)[k] : undefined), obj);
}

function set_path(obj: Obj, path: string, value: unknown): Obj {
  const keys = path.split('.');
  const copy: Obj = { ...obj };
  let cur = copy;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i]!;
    cur[k] = { ...((cur[k] as Obj) ?? {}) };
    cur = cur[k] as Obj;
  }
  cur[keys[keys.length - 1]!] = value;
  return copy;
}

// --- website-import reconciliation (schema-driven; issue #15 WS2) ---
function is_empty(v: unknown): boolean {
  return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
}

// Flatten a nested import draft into dotted leaf paths (arrays kept whole).
function flatten_draft(obj: unknown, prefix = '', out: Obj = {}): Obj {
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    for (const [k, v] of Object.entries(obj as Obj)) {
      flatten_draft(v, prefix ? `${prefix}.${k}` : k, out);
    }
  } else if (prefix) {
    out[prefix] = obj;
  }
  return out;
}

// Index every leaf/array FieldNode by its dotted path (walks objects + union variants).
function index_fields(node: FieldNode, out: Map<string, FieldNode> = new Map()): Map<string, FieldNode> {
  if (node.fields) for (const f of node.fields) index_fields(f, out);
  else if (node.variants) for (const v of node.variants) index_fields(v, out);
  else if (node.path) out.set(node.path, node);
  return out;
}

function show(v: unknown): string {
  if (v === undefined || v === null || v === '') return '—';
  return typeof v === 'string' ? v : JSON.stringify(v);
}

export default function SchemaForm({ tenant_id, app_code }: { tenant_id: string; app_code: string }): ReactElement {
  const qc = useQueryClient();
  const schemaQ = useQuery({
    queryKey: ['app-schema', app_code],
    queryFn: () => api.get<SchemaResp>(`/api/apps/${app_code}/schema`),
  });
  const dataQ = useQuery({
    queryKey: ['tenant-app', tenant_id, app_code],
    queryFn: () => api.get<DataResp>(`/api/tenants/${tenant_id}/apps/${app_code}`),
  });

  const [patch, setPatch] = useState<Obj>({});
  const [error, setError] = useState<string | null>(null);
  const [import_url, set_import_url] = useState('');
  const [importing, set_importing] = useState(false);
  const [import_msg, set_import_msg] = useState<string | null>(null);
  const [import_err, set_import_err] = useState<string | null>(null);
  const [suggestions, set_suggestions] = useState<Obj>({});
  const field_index = useMemo(
    () => (schemaQ.data ? index_fields(schemaQ.data.schema) : new Map<string, FieldNode>()),
    [schemaQ.data],
  );

  const save = useMutation({
    mutationFn: () => api.patch<DataResp>(`/api/tenants/${tenant_id}/apps/${app_code}`, { patch }),
    onSuccess: () => {
      setPatch({});
      setError(null);
      qc.invalidateQueries({ queryKey: ['tenant-app', tenant_id, app_code] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.reason : String(e)),
  });

  if (schemaQ.isLoading || dataQ.isLoading) return <p className="muted">Loading app data…</p>;
  if (schemaQ.error || dataQ.error) {
    const e = schemaQ.error ?? dataQ.error;
    return <p className="error">Failed to load: {e instanceof ApiError ? e.reason : String(e)}</p>;
  }

  const root = schemaQ.data!.schema;
  const data = dataQ.data!.data ?? {};
  const get_val = (path: string): unknown => {
    const p = get_path(patch, path);
    return p !== undefined ? p : get_path(data, path);
  };
  const set_val = (path: string, v: unknown): void => setPatch((prev) => set_path(prev, path, v));
  const dirty = Object.keys(patch).length > 0;

  // Website import: fill empty fields, union-merge arrays, and surface populated-but-different
  // fields as suggestions for the operator to review (never blind-overwrites). Writes into the
  // edit `patch` — the operator still reviews and clicks Save.
  async function run_import(): Promise<void> {
    set_importing(true);
    set_import_err(null);
    set_import_msg(null);
    try {
      const { draft } = await api.post<{ draft: Obj }>('/api/extract/business-profile', {
        url: import_url.trim(),
        app_code,
      });
      const flat = flatten_draft(draft);
      const next_sugg: Obj = { ...suggestions };
      let filled = 0;
      for (const [path, dval] of Object.entries(flat)) {
        const node = field_index.get(path);
        if (!node || node.editable === 'never' || dval === undefined || dval === null) continue;
        const cur = get_val(path);
        if (node.type === 'array') {
          const cur_arr = Array.isArray(cur) ? (cur as unknown[]) : [];
          const add = Array.isArray(dval) ? (dval as unknown[]) : [dval];
          const seen = new Set(cur_arr.map((x) => JSON.stringify(x)));
          const merged = [...cur_arr];
          for (const x of add) {
            const k = JSON.stringify(x);
            if (!seen.has(k)) {
              seen.add(k);
              merged.push(x);
            }
          }
          if (merged.length !== cur_arr.length) {
            set_val(path, merged);
            filled++;
          }
        } else if (is_empty(cur)) {
          set_val(path, dval);
          filled++;
        } else if (cur !== dval) {
          next_sugg[path] = dval;
        }
      }
      set_suggestions(next_sugg);
      const n = Object.keys(next_sugg).length;
      set_import_msg(
        `Filled ${filled} empty field${filled === 1 ? '' : 's'}` +
          (n ? `; ${n} populated field${n === 1 ? '' : 's'} to review below.` : '. Review + Save.'),
      );
    } catch (err) {
      set_import_err(err instanceof ApiError ? err.reason : String(err));
    } finally {
      set_importing(false);
    }
  }

  function accept_suggestion(path: string): void {
    set_val(path, suggestions[path]);
    set_suggestions(({ [path]: _drop, ...rest }) => rest);
  }
  function dismiss_suggestion(path: string): void {
    set_suggestions(({ [path]: _drop, ...rest }) => rest);
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          aria-label="Website URL to import"
          style={{ flex: '1 1 240px' }}
          placeholder="https://business-website.com — import to fill empty fields"
          value={import_url}
          onChange={(e) => set_import_url(e.target.value)}
        />
        <button type="button" disabled={importing || import_url.trim() === ''} onClick={() => void run_import()}>
          {importing ? 'Importing…' : 'Import from website'}
        </button>
        {import_msg && <span className="muted" style={{ fontSize: 12 }}>{import_msg}</span>}
        {import_err && <span className="error" style={{ fontSize: 12 }}>{import_err}</span>}
      </div>
      {Object.keys(suggestions).length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <strong style={{ fontSize: 13 }}>Imported values that differ from existing — review:</strong>
          {Object.entries(suggestions).map(([path, val]) => (
            <div key={path} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 13, flexWrap: 'wrap' }}>
              <span style={{ minWidth: 150, fontWeight: 600 }}>{field_index.get(path)?.label ?? path}</span>
              <span className="muted">now: {show(get_val(path))}</span>
              <span>→ {show(val)}</span>
              <button type="button" onClick={() => accept_suggestion(path)}>Use imported</button>
              <button type="button" className="secondary" onClick={() => dismiss_suggestion(path)}>Keep</button>
            </div>
          ))}
        </div>
      )}
      <SharedForm
        schema={root}
        getValue={get_val}
        setValue={set_val}
        widgets={OFFICE_WIDGETS}
        showEditableTag
      />
      {error && <div className="error" style={{ marginBottom: 12 }}>Save failed: {error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
        {dirty && !save.isPending && (
          <button className="secondary" onClick={() => { setPatch({}); setError(null); }}>
            Discard
          </button>
        )}
      </div>
    </div>
  );
}
